import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { fetchHomeDataThunk, makeFetchSlice } from 'app/redux-fetch'
import { AppDispatch } from 'app/store'
import {
  detachPaymentMethod,
  getPaymentMethods,
  setDefaultPaymentMethod,
} from './api'

import {
  DEFAULT_PAYMENT_METHODS_REDUCER_NAME,
  DETACH_PAYMENT_METHODS_REDUCER_NAME,
  PAYMENT_METHODS_LIST_REDUCER_NAME,
  SELECTED_PAYMENT_METHOD_REDUCER_NAME,
} from './consts'
import type { PaymentMethodType } from './types'

interface SelectedPaymentMethodState {
  id: string | null
}

const SELECT_PAYMENT_METHOD_INIT_STATE = {
  id: null,
} as SelectedPaymentMethodState

const selectedPaymentMethodSlice = createSlice({
  name: SELECTED_PAYMENT_METHOD_REDUCER_NAME,
  initialState: SELECT_PAYMENT_METHOD_INIT_STATE,
  reducers: {
    selectPaymentMethod(state, action: PayloadAction<string>) {
      state.id = action.payload
    },
    clear: () => SELECT_PAYMENT_METHOD_INIT_STATE,
  },
})

export const selectedPaymentMethodActions = selectedPaymentMethodSlice.actions
export const selectedPaymentMethodReducer = selectedPaymentMethodSlice.reducer

const paymentMethodsListSlice = makeFetchSlice(
  PAYMENT_METHODS_LIST_REDUCER_NAME
)
export const paymentMethodsListReducer = paymentMethodsListSlice.reducer
export const getPaymentMethodsListActions = paymentMethodsListSlice.actions

export const getPaymentMethodsListThunk = (rootDomainId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      const res = await fetchHomeDataThunk({
        dispatch,
        actions: getPaymentMethodsListActions,
        asyncFunc: () => getPaymentMethods(rootDomainId),
      })
      const defaultPaymentMethod = res.find(
        (e: PaymentMethodType) => e.isDefault
      )?.id

      if (defaultPaymentMethod) {
        dispatch(
          selectedPaymentMethodActions.selectPaymentMethod(defaultPaymentMethod)
        )
      } else {
        dispatch(selectedPaymentMethodActions.clear())
      }
      return res
    } catch (error) {
      console.log('Error getting payment methods', error)
    }
  }
}

const defaultPaymentMethodsSlice = makeFetchSlice(
  DEFAULT_PAYMENT_METHODS_REDUCER_NAME
)
export const defaultPaymentMethodsReducer = defaultPaymentMethodsSlice.reducer
export const defaultPaymentMethodsActions = defaultPaymentMethodsSlice.actions

export const setDefaultPaymentMethodThunk = (id: string, domainId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchHomeDataThunk({
        dispatch,
        actions: defaultPaymentMethodsActions,
        asyncFunc: () => setDefaultPaymentMethod(id, domainId),
      })
      dispatch(getPaymentMethodsListThunk(domainId))
    } catch (error) {
      console.log('Error setting default payment method', error)
    }
  }
}

const detachPaymentMethodsSlice = makeFetchSlice(
  DETACH_PAYMENT_METHODS_REDUCER_NAME
)
export const detachPaymentMethodsReducer = detachPaymentMethodsSlice.reducer
export const detachPaymentMethodsActions = detachPaymentMethodsSlice.actions

export const detachPaymentMethodThunk = (id: string, rootDomainId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchHomeDataThunk({
        dispatch,
        actions: detachPaymentMethodsActions,
        asyncFunc: () => detachPaymentMethod(id, rootDomainId),
      })
      dispatch(getPaymentMethodsListThunk(rootDomainId))
    } catch (error) {
      console.log('Error detaching default payment method', error)
    }
  }
}
